import { css } from 'aphrodite';
import { FC, useId, useState } from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { DashboardCard, DashboardCardHeaderLabel } from '../DashboardCard';
import { AgentPanelSkeleton } from './AgentPanel';
import { AgentTile, AgentTileSkeleton } from './AgentTile';
import { useEmailExecAgent } from './agents/EmailExec';
import { useKeepInTouchAgent } from './agents/KeepInTouch';
import { useMeetingPrepAgent } from './agents/MeetingPrep';
import { styleSheet } from './styles';

const TELEMETRY_CATEGORY = 'DashboardAgentsCard';

export const DashboardAgentsCard: FC = () => {
	const { logEvent } = useEventLogging(TELEMETRY_CATEGORY);
	const panelId = useId();
	const [openKey, setOpenKey] = useState<string | null>(null);
	const emailExec = useEmailExecAgent();
	const meetingPrep = useMeetingPrepAgent();
	const keepInTouch = useKeepInTouchAgent();
	const agents = [emailExec, meetingPrep, keepInTouch].filter(agent => agent !== null);

	if (agents.length === 0) {
		return null;
	}

	const openAgent = agents.find(agent => agent.key === openKey);
	const toggle = (key: string) => {
		const opening = key !== openKey;
		logEvent(opening ? 'AgentOpened' : 'AgentClosed', { agent: key });
		setOpenKey(opening ? key : null);
	};

	return (
		<DashboardCard styleDeclaration={styleSheet.card}>
			<DashboardCardHeaderLabel styleDeclaration={styleSheet.cardHeader}>
				<span className={css(styleSheet.statusDot)} />
				<span>Your agents are working</span>
			</DashboardCardHeaderLabel>
			<div className={css(styleSheet.tiles)}>
				{agents.map(agent =>
					agent.tile ? (
						<AgentTile
							key={agent.key}
							caption={agent.tile.caption}
							count={agent.tile.count}
							icon={agent.icon}
							iconStyles={agent.iconStyles}
							isError={agent.tile.isError}
							isOpen={agent.key === openKey}
							name={agent.name}
							onToggle={() => toggle(agent.key)}
							panelId={panelId}
						/>
					) : (
						<AgentTileSkeleton key={agent.key} />
					)
				)}
			</div>
			{openAgent ? (
				<section id={panelId} aria-label={openAgent.name} className={css(styleSheet.panel)}>
					{openAgent.panel ?? <AgentPanelSkeleton />}
				</section>
			) : null}
		</DashboardCard>
	);
};
